import { useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useNeedStore } from '../store/needStore';
import { useMatchStore } from '../store/matchStore';
import { useUserDataStore } from '../store/userDataStore';
import { useLanguageStore } from '../store/languageStore';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
}

interface UseChatReturn {
  messages: ChatMessage[];
  isLoading: boolean;
  error: string | null;
  sendMessage: (message: string) => Promise<void>;
  clearChat: () => void;
}

export const useChat = (): UseChatReturn => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  
  const { selectedNeed } = useNeedStore();
  const { matchResults } = useMatchStore();
  const { zipCode } = useUserDataStore();
  const { selectedLanguage } = useLanguageStore();

  const clearChat = useCallback(() => {
    setMessages([]);
    setSessionId(null);
    setError(null);
  }, []);

  const sendMessage = useCallback(async (message: string) => {
    if (!message.trim() || isLoading) return;

    setIsLoading(true);
    setError(null);

    // Add user message immediately
    const userMessage: ChatMessage = {
      role: 'user',
      content: message,
      timestamp: Date.now()
    };
    setMessages(prev => [...prev, userMessage]);

    try {
      // Generate session ID if not exists
      if (!sessionId) {
        setSessionId(`session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
      }

      // Get user context for better AI responses
      const userContext = {
        needs: selectedNeed ? [selectedNeed] : [],
        location: zipCode || 'Not specified',
        availablePrograms: matchResults.slice(0, 3).map(program => program.name)
      };

      // Use ai-proxy function for chat (which we know is working)
      const { data, error: supabaseError } = await supabase.functions.invoke('ai-proxy', {
        body: {
          type: 'chat',
          data: {
            prompt: message,
            language: selectedLanguage,
            context: userContext
          }
        }
      });

      if (supabaseError) {
        throw new Error(`Chat service error: ${supabaseError.message}`);
      }

      if (!data || !data.response) {
        throw new Error('No response from chat service');
      }

      // Add AI response
      const aiMessage: ChatMessage = {
        role: 'assistant',
        content: data.response,
        timestamp: Date.now()
      };
      setMessages(prev => [...prev, aiMessage]);

    } catch (err) {
      console.error('Chat error:', err);
      setError(err instanceof Error ? err.message : 'Failed to send message');
      
      // Add error message to chat with more helpful information
      const errorMessage: ChatMessage = {
        role: 'assistant',
        content: `I apologize, but I'm having trouble connecting right now. This might be due to a configuration issue with the AI service. 

For immediate help, you can:
1. Call the programs directly using the phone numbers provided
2. Try refreshing the page and trying again
3. Contact support if the issue persists

Error details: ${err instanceof Error ? err.message : 'Unknown error'}`,
        timestamp: Date.now()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, sessionId, selectedNeed, matchResults, zipCode, selectedLanguage]);

  return {
    messages,
    isLoading,
    error,
    sendMessage,
    clearChat
  };
};